import React, { useState } from 'react';
import moment from 'moment';
import 'moment/locale/ru';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { withStyles, makeStyles, lighten } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import TableFooter from '@material-ui/core/TableFooter';
import TablePagination from '@material-ui/core/TablePagination';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';

import TablePaginationActions from '../../components/pagination';

import { useAPI } from '../../api/contracts/use-api';
import {EContractStatus} from "../../enum/contractor-types";

moment.locale('ru');

const useToolbarStyles = makeStyles((theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  highlight:
    theme.palette.type === 'light'
      ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark,
        },
  title: {
    flex: '1 1 100%',
  },
}));

const EnhancedTableToolbar = (props) => {
  const classes = useToolbarStyles();

  return (
    <Toolbar>
      <Typography className={classes.title} variant="h6" id="tableTitle" component="div">
        Договора
      </Typography>
    </Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: '#F0F0F0',
    color: theme.palette.common.black,
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    '&:nth-of-type(odd)': {},
  },
}))(TableRow);

const useStyles = makeStyles({
  table: {
    minWidth: 700,
  },
});

const headCells = [
  { id: 'number', numeric: false, disablePadding: false, label: 'Номер договора' },
  { id: 'contractor.name', numeric: false, disablePadding: false, label: 'Имя контрагента' },
  { id: 'share.cadastral', numeric: false, disablePadding: false, label: 'Кадастровый номер' },
  { id: 'signingDate', numeric: false, disablePadding: false, label: 'Дата подписания' },
  { id: 'endDate', numeric: false, disablePadding: false, label: 'Дата окончания' },
  { id: 'audit.createdDate', numeric: false, disablePadding: false, label: 'Дата создания' },
  { id: 'expirationDays', numeric: false, disablePadding: false, label: 'Осталось дней' },
];

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'right'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

export const Contracts = () => {
  const classes = useStyles();

  const history = useHistory();

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(8);

  const [order, setOrder] = React.useState('desc');
  const [orderBy, setOrderBy] = React.useState('audit.createdDate');

  const { data, isLoading } = useAPI({ page, size, orderBy, order });

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <>
      <div style={{ padding: 8 }}>
        <Button variant="outlined" onClick={() => history.push('/contract-editor')}>
          Создать
        </Button>
      </div>
      <EnhancedTableToolbar />

      <TableContainer>
        <Table className={classes.table} stickyHeader aria-label="sticky table">
          <EnhancedTableHead
            classes={classes}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
          />

          <TableBody >
            {data.content.map((row) => (
              <StyledTableRow
                style={{ cursor: 'pointer', backgroundColor: getRowColor(row.status) }}
                hover
                key={row.id}
                onClick={() => history.push('/contract-editor/' + row.id)}
              >
                <StyledTableCell align="right">{row.number}</StyledTableCell>
                <StyledTableCell align="right">{row.contractor.name}</StyledTableCell>
                <StyledTableCell align="right">{row.share.name || row.share.id}</StyledTableCell>
                <StyledTableCell align="right">
                  {moment.utc(row.signingDate).local().format('LL')}
                </StyledTableCell>
                <StyledTableCell align="right">
                  {moment.utc(row.endDate).local().format('LL')}
                </StyledTableCell>
                <StyledTableCell align="right">
                  {moment.utc(row.createdDate).local().format('LL')}
                </StyledTableCell>
                <StyledTableCell align="right">
                  {moment(row.endDate).diff(moment(row.signingDate), 'days')}
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>

          <TableFooter>
            <TableRow>
              <TablePagination
                labelRowsPerPage={'Кол-во на странице'}
                rowsPerPageOptions={[8, 10, 25, { label: 'All', value: -1 }]}
                colSpan={7}
                count={data.totalElements}
                rowsPerPage={size}
                page={page}
                onPageChange={(e, page) => setPage(page)}
                onRowsPerPageChange={(e) => {
                  setSize(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                ActionsComponent={TablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </>
  );
};

function getRowColor(status: EContractStatus) {
  switch (status) {
    case EContractStatus.CREATED:
      return '#F0F0F0';
    case EContractStatus.ACTIVE:
      return 'rgb(67, 160, 71, 0.5)';
    default:
      return 'white';
  }
}
